// packages/shared/src/utils/timeUtils.js

/**
 * Format military time to 12-hour format with AM/PM
 * @param {string} militaryTime - Time in HH:MM format (e.g., "14:30")
 * @returns {string} - Time in 12-hour format (e.g., "2:30 PM")
 */
export const formatTimeToDisplay = (militaryTime) => {
    if (!militaryTime) return '';
    
    const [hours, minutes] = militaryTime.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };
  
  /**
   * Convert 12-hour time to military format
   * @param {string} time12h - Time in 12-hour format (e.g., "2:30 PM")
   * @returns {string} - Time in HH:MM format
   */
  export const formatTimeToMilitary = (time12h) => {
    if (!time12h) return '';
    
    const [time, period] = time12h.split(' ');
    const [hours, minutes] = time.split(':').map(Number);
    
    let militaryHours = hours;
    if (period === 'PM' && hours !== 12) {
      militaryHours = hours + 12;
    } else if (period === 'AM' && hours === 12) {
      militaryHours = 0;
    }
    
    return `${militaryHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };
  
  /**
   * Combine date and time strings into a Unix timestamp
   * @param {string} date - Date in YYYY-MM-DD format
   * @param {string} time - Time in HH:MM format
   * @param {string} timezone - IANA timezone string (optional, defaults to local)
   * @returns {number} - Unix timestamp in milliseconds
   */
  export const createTimestamp = (date, time, timezone = null) => {
    if (!date) return null;
    
    // If no time provided, use start of day
    const timeStr = time || '00:00';
    
    // Create date string in ISO format
    const dateTimeStr = `${date}T${timeStr}:00`;
    
    // Create Date object (will use local timezone by default)
    const dateObj = new Date(dateTimeStr);
    
    // Return Unix timestamp (milliseconds)
    return dateObj.getTime();
  };
  
  /**
   * Convert Unix timestamp to date string
   * @param {number} timestamp - Unix timestamp in milliseconds
   * @returns {string} - Date in YYYY-MM-DD format
   */
  export const timestampToDate = (timestamp) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  };
  
  /**
   * Convert Unix timestamp to time string
   * @param {number} timestamp - Unix timestamp in milliseconds
   * @returns {string} - Time in HH:MM format
   */
  export const timestampToTime = (timestamp) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    return `${hours}:${minutes}`;
  };
  
  /**
   * Convert Unix timestamp to display time (12-hour format)
   * @param {number} timestamp - Unix timestamp in milliseconds
   * @returns {string} - Time in 12-hour format with AM/PM
   */
  export const timestampToDisplayTime = (timestamp) => {
    if (!timestamp) return '';
    
    const militaryTime = timestampToTime(timestamp);
    return formatTimeToDisplay(militaryTime);
  };
  
  /**
   * Get current time in HH:MM format
   * @returns {string} - Current time in HH:MM format
   */
  export const getCurrentTime = () => {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };
  
  /**
   * Get current date in YYYY-MM-DD format
   * @returns {string} - Current date in YYYY-MM-DD format
   */
  export const getCurrentDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  /**
   * Format date and time for display
   * @param {string} date - Date in YYYY-MM-DD format
   * @param {string} time - Time in HH:MM format
   * @returns {string} - Formatted date and time
   */
  export const formatDateTime = (date, time) => {
    if (!date) return '';
    
    const dateObj = new Date(date);
    const formattedDate = dateObj.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
    
    const formattedTime = time ? formatTimeToDisplay(time) : '';
    
    return formattedTime ? `${formattedDate} at ${formattedTime}` : formattedDate;
  };
  
  /**
   * Calculate duration between two times in minutes
   * @param {string} startTime - Start time in HH:MM format
   * @param {string} endTime - End time in HH:MM format
   * @returns {number} - Duration in minutes
   */
  export const calculateDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return 0;
    
    const [startHours, startMinutes] = startTime.split(':').map(Number);
    const [endHours, endMinutes] = endTime.split(':').map(Number);
    
    const startTotalMinutes = startHours * 60 + startMinutes;
    const endTotalMinutes = endHours * 60 + endMinutes;
    
    return endTotalMinutes - startTotalMinutes;
  };
  
  /**
   * Add minutes to a time string
   * @param {string} time - Time in HH:MM format
   * @param {number} minutes - Minutes to add
   * @returns {string} - New time in HH:MM format
   */
  export const addMinutesToTime = (time, minutes) => {
    if (!time) return '';
    
    const [hours, mins] = time.split(':').map(Number);
    const date = new Date(2000, 0, 1, hours, mins);
    date.setMinutes(date.getMinutes() + minutes);
    
    const newHours = date.getHours().toString().padStart(2, '0');
    const newMinutes = date.getMinutes().toString().padStart(2, '0');
    
    return `${newHours}:${newMinutes}`;
  };
  
  /**
   * Get user's current timezone
   * @returns {string} - IANA timezone string
   */
  export const getUserTimezone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  };